import { formatMinor } from '../format'
import type { BalBlock } from '../spec/types'

/**
 * A balance shown as its arithmetic, never as a single number.
 *
 * Verbatim from `docs/design/reference/blocks/bal.html`. A row of equal-width
 * legs over a 1px-gap lined background, then a footer carrying the formula in
 * mono and the binding statement pushed to the right.
 *
 * This block exists for invariant 2c / law 4: `available = min(ledger_balance −
 * pending_out, provider_available)`. The `min()` is the safety property that
 * stops a discrepancy letting a customer spend money FinXP does not show, and
 * it is only a safety property if an operator can see which side won. So every
 * leg of the comparison is on screen at once — ours, the provider's, the
 * binding result, and anything held out — with the `formula` line written out
 * underneath so the arithmetic is auditable on the screen itself rather than
 * taken on trust. Collapsing this to one figure would be a redesign, not a
 * tidy-up.
 *
 * The same block does the reconciliation invariant on the dashboard: Σ customer
 * ledger balances against the FinXP program record, with the delta as its own
 * leg because zero is the only acceptable value and a zero has to be visible to
 * be reassuring.
 */
export function Bal({ block }: { block: BalBlock }) {
  return (
    <div style={{ border: '1px solid var(--k-line)', background: 'var(--k-panel)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          flexWrap: 'wrap',
          gap: '1px',
          background: 'var(--k-line)',
        }}
      >
        {block.legs.map((leg) => (
          // `data-edge` paints a 2px inset rule along the top of the leg in its
          // tone — the authoritative and binding legs read green, an excluded
          // or stale one reads amber, before a single word is read.
          <div
            key={leg[0]}
            data-edge={leg[6]}
            style={{
              flex: 1,
              minWidth: '190px',
              background: 'var(--k-panel)',
              padding: '11px 12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span
                style={{
                  fontSize: '10.5px',
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                  color: 'var(--k-mut)',
                }}
              >
                {leg[0]}
              </span>
              <span
                data-tone={leg[5]}
                style={{
                  padding: '0 4px',
                  fontSize: '9px',
                  fontWeight: 800,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                }}
              >
                {leg[4]}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', marginTop: '7px' }}>
              {/* The digits and the ISO code are two spans, deliberately: the
                  code is never merged into the formatted number and never
                  dropped (invariant 4), and minor units are scaled by the
                  ISO-4217 exponent rather than assumed to be two places
                  (invariant 3). `data-amt='excl'` strikes an amount through to
                  say it is held out of available — the projector's default is
                  `auth`, which paints nothing. */}
              <span
                data-amt={leg[7] ?? 'auth'}
                style={{ fontWeight: 800, fontSize: '21px', letterSpacing: '-.01em' }}
              >
                {formatMinor(leg[1], leg[2])}
              </span>
              <span
                style={{ fontFamily: 'var(--k-mono)', fontSize: '11px', color: 'var(--k-mut)' }}
              >
                {leg[2]}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--k-mut)', marginTop: '5px' }}>{leg[3]}</div>
          </div>
        ))}
      </div>
      {/* The formula is the provenance of the headline figure and the binding
          line is its age — `Provider snapshot 4m old · refresh on open`. A
          balance whose staleness is not stated is a balance an operator will
          quote to a customer. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 12px',
          borderTop: '2px solid var(--k-rule)',
          background: 'var(--k-bg)',
        }}
      >
        <span style={{ fontFamily: 'var(--k-mono)', fontSize: '11.5px' }}>{block.formula}</span>
        <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: 'var(--k-mut)' }}>
          {block.binding}
        </span>
      </div>
    </div>
  )
}
